import React, { useRef, useState } from "react";
import { Box, Divider, Grid, Typography, Button } from "@mui/material";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import DOMPurify from "dompurify";

import QTDetailsImageWithLabelSection from "../../../../common/QTDetailsImageWithLabelSection";
import QTDetailsAudioWithLabelSection from "../../../../common/QTDetailsAudioWithLabelSection";
import ExternalNarratorTextField from "./ExternalNarratorTextField";
import AudioRecordingModalNarrator from "../../../../common/AudioRecordingModalNarrator";
import { images } from "../../../../constants";

const labelSx = {
  width: 110,
  minWidth: 110,
  fontSize: 14,
  fontWeight: 600,
  color: "#020303",
  fontFamily: "Avenir Book, sans-serif",
};

const valueSx = {
  fontSize: 14,
  fontWeight: 400,
  color: "#020303",
  fontFamily: "Avenir Book, sans-serif",
  whiteSpace: "pre-line",
};

const valueCellSx = {
  flex: 1,
  maxWidth: 420,
};

const ExternalNarratorDataFieldsSection = ({
  quicktaleDetails,
  setQuicktaleDetails,
  greetingHtml,
  narratorDisplayName,
  submissionStatus,
  canEditPhoto,
  canEditAudio,
  canEditTitle,
  canEditDescription,
  uploadImage,
  setUploadImage,
  uploadAudioFile,
  setUploadAudioFile,
  onSubmit,
  submitDisabled,
  submitLabel,
  handleImagePreviewModalOpen,
  handleAudioModalOpen,
  handleFileUpload,
}) => {
  const fileInputRef = useRef(null);
  const [isRecordingModalOpen, setRecordingModalOpen] = useState(false);

  const imageSrc =
    quicktaleDetails?.photoUrl
      ? quicktaleDetails.photoUrl
      : quicktaleDetails?.imageUrls?.[0]
        ? quicktaleDetails.imageUrls[0]
        : null;

  const hasRecording = !!quicktaleDetails?.audioUrl;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileData = {
      preview: URL.createObjectURL(file),
      raw: file,
    };

    setUploadAudioFile(fileData);
    setQuicktaleDetails({
      ...quicktaleDetails,
      originalAudioName: file.name,
    });
    // Maintain existing upload behavior via container callback
    handleFileUpload?.(fileData, "original");
  };

  const handleRecordingComplete = (audioBlob, durationSeconds) => {
    const mimeType = audioBlob?.type || "audio/webm";

    const extFromMime = (type) => {
      if (!type) return "webm";
      if (type.includes("mp4")) return "mp4";
      if (type.includes("m4a")) return "m4a";
      if (type.includes("aac")) return "aac";
      if (type.includes("ogg")) return "ogg";
      if (type.includes("wav")) return "wav";
      if (type.includes("webm")) return "webm";
      return "webm";
    };

    const title = quicktaleDetails?.title?.trim();
    const safeBaseName = title
      ? title
      : `Untitled ${new Date()
          .toLocaleString()
          .replace(/[/\\?%*:|"<>]/g, "-")}`;

    const extension = extFromMime(mimeType);
    const fileName = `${safeBaseName}.${extension}`;
    const file = new File([audioBlob], fileName, { type: mimeType });

    const fileData = {
      preview: URL.createObjectURL(file),
      raw: file,
      durationSeconds: typeof durationSeconds === 'number' ? durationSeconds : undefined,
    };

    setUploadAudioFile(fileData);
    setQuicktaleDetails({
      ...quicktaleDetails,
      originalAudioName: file.name,
      ...(typeof durationSeconds === 'number' ? { audioLength: durationSeconds } : {}),
    });
    handleFileUpload?.(fileData, "original");
  };

//   Upload button
//              <Button
//                variant="contained"
//                onClick={handleUploadClick}
//                disabled={!canEditAudio}
//                sx={{
//                  bgcolor: "#00A7CD",
//                  color: "#fff",
//                  fontWeight: 700,
//                  px: 3,
//                  borderRadius: 1,
//                  "&:hover": { bgcolor: "#008bb0" },
//                }}
//              >
//                UPLOAD
//              </Button>

  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      {/* Upper section: greeting + instructions OR submission status messaging */}
      {submissionStatus === 'submitting' ? (
        <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
          <Typography sx={{ fontSize: 20, color: '#fff', backgroundColor: '#7f3c97', padding: 2, borderRadius: 10  }}>
            Submission in progress. Please stay on this page until your submission is complete...
          </Typography>
        </Box>
      ) : submissionStatus === 'complete' ? (
        <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
          <Typography sx={{ fontSize: 20, color: '#9447AF' }}>
            Thank you for submitting your story!
          </Typography>
          <Typography sx={{ ...valueSx, fontSize: 16, mt: 3 }}>
               If you need additional help: email{" "}
              <a href="mailto:support@quicktales.com" style={{ color: "#00A7CD" }}>
                support@quicktales.com
              </a>
              {" "}or call{" "}
              <a href="tel:+14242741055" style={{ color: "#00A7CD" }}>
                424-274-1055
              </a>.
          </Typography>
        </Box>
      ) : (
        <>
          {greetingHtml ? (
            <Typography
              sx={{
                ...valueSx,
                fontSize: 16,
                mb: 1.5,
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(greetingHtml, {
                  USE_PROFILES: { html: true },
                }),
              }}
            />
          ) : null}

          {/* Instructions + Controls */}
          <Box sx={{ mb: 1.5 }}>
            {/* Typography sx={{ ...valueSx, fontSize: 16, mb: 0.5 }}>
              To record your story:
            </Typography>
            <Typography sx={{ ...valueSx, fontSize: 16, mb: 0.5, ml: 2 }}>
              1. Tap the <strong>RECORD</strong> button.
            </Typography>
            <Typography sx={{ ...valueSx, fontSize: 16, mb: 3, ml: 2 }}>
              2. When finished, tap <strong>SUBMIT</strong>.
            </Typography> */}

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 1.25 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                style={{ display: "none" }}
                onChange={handleUploadChange}
              />

              <Button
                variant="contained"
                onClick={() => setRecordingModalOpen(true)}
                disabled={!canEditAudio}
                sx={{
                  bgcolor: "#00A7CD",
                  color: "#fff",
                  fontWeight: 700,
                  px: 3,
                  borderRadius: 1,
                  width: '160px',
                  "&:hover": { bgcolor: "#008bb0" },
                }}
              >
                RECORD AUDIO
              </Button>
                        {submissionStatus === 'idle' ? (
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={!!submitDisabled}
              sx={{
                bgcolor: "#9447AF",
                color: "#fff",
                fontWeight: 700,
                px: 3,
                borderRadius: 1,
                width: '160px',
                "&:hover": { bgcolor: "#7f3c97" },
              }}
            >
              {submitLabel || "SUBMIT"}
            </Button>
          ) : null}
            </Box>
          </Box>
        </>
      )}
      <Divider sx={{ mb: 2, mt: 2 }} />
      {/* Photo (no label/no filename) */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
        <QTDetailsImageWithLabelSection
          label={""}
          image={imageSrc}
          fileName={""}
          editEnabled={!!canEditPhoto}
          details={quicktaleDetails}
          setDetails={setQuicktaleDetails}
          uploadImage={uploadImage}
          setUploadImage={setUploadImage}
          isCreateMode={true}
          autoOpenCropOnUpload={true}
          disablePreview={true}
        />
      </Box>

      {/* Title */}
      <Grid item sx={{ display: "flex", alignItems: "baseline" }}>
        <Typography component="span" sx={labelSx}>
          Title:
        </Typography>
        {canEditTitle ? (
          <Box sx={valueCellSx}>
            <ExternalNarratorTextField
              value={quicktaleDetails?.title}
              disabled={false}
              capitalizeWords
              onChange={(e) =>
                setQuicktaleDetails({
                  ...quicktaleDetails,
                  title: e.target.value,
                })
              }
            />
          </Box>
        ) : (
          <Box sx={valueSx}>{quicktaleDetails?.title || ""}</Box>
        )}
      </Grid>

      <Grid container direction="column" sx={{ gap: 1.25, marginTop: '12px' }}>

        {/* Description */}
        <Grid item sx={{ display: "flex", alignItems: "flex-start" }}>
          <Typography component="span" sx={{ ...labelSx, mt: "2px" }}>
            Description:
          </Typography>
          {canEditDescription ? (
            <Box sx={valueCellSx}>
              <ExternalNarratorTextField
                value={quicktaleDetails?.description}
                multiline={true}
                rows={5}
                disabled={false}
                onChange={(e) =>
                  setQuicktaleDetails({
                    ...quicktaleDetails,
                    description: e.target.value,
                  })
                }
              />
            </Box>
          ) : (
            <Box sx={valueSx}>{quicktaleDetails?.description || ""}</Box>
          )}
        </Grid>

        {/* Narrator (not editable) */}
        <Grid item sx={{ display: "flex", alignItems: "baseline" }}>
          <Typography component="span" sx={labelSx}>
            Narrator:
          </Typography>
          <Typography component="span" sx={valueSx}>
            {narratorDisplayName || ""}
          </Typography>
        </Grid>


        {/* Recording */}
        <Grid item sx={{ display: "flex", alignItems: "center" }}>
          <Typography component="span" sx={labelSx}>
            Recording:
          </Typography>
          {hasRecording ? (
            <PlayCircleFilledWhiteOutlinedIcon
              fontSize="large"
              onClick={handleAudioModalOpen}
              sx={{ color: "#00A7CD", cursor: "pointer", mr: 1 }}
            />
          ) : null}
        </Grid>

        <Grid item sx={{ alignItems: 'center', textAlign: 'center' }}>

        </Grid>
      </Grid>

      {submissionStatus === 'idle' ? (
        <Typography sx={{ ...valueSx, fontSize: 16, mt: 3 }}>
            For help: email{" "}
            <a href="mailto:support@quicktales.com" style={{ color: "#00A7CD" }}>
              support@quicktales.com
            </a>
            {" "}or call{" "}
            <a href="tel:+14242741055" style={{ color: "#00A7CD" }}>
              424-274-1055
            </a>.
        </Typography>
      ) : null}

      {/* Keep underlying upload/record logic (hidden UI for now; used in next step) */}
      <Box sx={{ display: "none" }}>
        <QTDetailsAudioWithLabelSection
          label={"Original Audio"}
          handleAudioModalOpen={handleAudioModalOpen}
          editEnabled={!!canEditAudio}
          details={quicktaleDetails}
          setDetails={setQuicktaleDetails}
          uploadAudioFile={uploadAudioFile}
          setUploadAudioFile={setUploadAudioFile}
          handleFileUpload={(file) => handleFileUpload(file, "original")}
          showProcessButton={false}
        />
      </Box>

      <AudioRecordingModalNarrator
        open={isRecordingModalOpen}
        handleClose={() => setRecordingModalOpen(false)}
        onRecordingComplete={handleRecordingComplete}
      />
    </Box>
  );
};

export default ExternalNarratorDataFieldsSection;
